import { Suspense, useMemo } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ConfigContext } from '@feed-widget/contexts/feedConfigContext'
import { PreviewFeedProvider } from '@feed-widget/contexts/PreviewFeedContext'
import { BettingContext } from '@feed-widget/providers/bettingContext'
import { SURFACE_REGISTRY, isDisplayType } from '@feed-widget/surfaces/registry'
import { PrepacksSurfaceView } from '@feed-widget/surfaces/PrepacksPresentation'

const PREVIEW_API_URL = 'studio-preview://feed'
const PREVIEW_ORG = 'studio-preview'
// Stands in for a real viewer token so identity-gated affordances (the follow
// button, chiefly) can be reviewed in Studio. It never reaches the network:
// every feed hook short-circuits on the preview context, and `useFollowState`
// keeps the toggle local rather than calling the feed API with it.
export const PREVIEW_VIEWER_TOKEN = 'studio-preview-viewer'
const PREVIEW_BETTING = {
  addToBetslip: () => true,
  addToCart: () => true,
  removeFromBetslip: () => {},
  type: 'custom',
  userData: null,
}

function createPreviewQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        staleTime: Infinity,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
    },
  })
}

function buildPreviewSurface(surface, displayType, showBettingChrome) {
  const hasBettingChromeOverride = typeof showBettingChrome === 'boolean'
  const previewShowsBettingChrome = hasBettingChromeOverride
    ? showBettingChrome
    : (surface ? Boolean(surface.show_betting_chrome) : true)
  return {
    id: surface?.id || 'preview-surface',
    slug: surface?.slug || `preview-${displayType}`,
    name: surface?.name || 'Preview',
    display_type: displayType,
    betting_mode: surface?.betting_mode || 'none',
    show_betting_chrome: previewShowsBettingChrome,
    theme_overrides: surface?.theme_overrides || null,
    config: surface?.config || {},
  }
}

export function WidgetPreviewProviders({
  branding,
  displayType = 'story_block',
  surface = null,
  organizationSlug = PREVIEW_ORG,
  items,
  showBettingChrome,
  viewerToken = null,
  children,
}) {
  const queryClient = useMemo(createPreviewQueryClient, [])
  const previewSurface = useMemo(
    () => buildPreviewSurface(surface, displayType, showBettingChrome),
    [surface, displayType, showBettingChrome]
  )
  const feedConfigValue = useMemo(
    () => ({
      organization: organizationSlug || PREVIEW_ORG,
      name: '',
      branding,
      surface: previewSurface,
      loading: false,
      error: null,
    }),
    [organizationSlug, branding, previewSurface]
  )

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigContext.Provider value={feedConfigValue}>
        <BettingContext.Provider value={PREVIEW_BETTING}>
          <PreviewFeedProvider items={items} token={viewerToken}>
            {children}
          </PreviewFeedProvider>
        </BettingContext.Provider>
      </ConfigContext.Provider>
    </QueryClientProvider>
  )
}

export default function WidgetSurfacePreview({
  branding,
  displayType = 'story_block',
  surface = null,
  organizationSlug = PREVIEW_ORG,
  items,
  fallback = null,
  showBettingChrome,
  viewerToken = null,
  contentMode = 'feed',
  prepacks = [],
  prepackTemplates = [],
  oddsFormat = 'american',
}) {
  if (contentMode === 'prepacks' && surface?.prepacks_enabled) {
    return (
      <div data-widget-surface-preview="prepacks">
        <PrepacksSurfaceView
          items={prepacks}
          templates={prepackTemplates}
          branding={branding}
          displayType={displayType}
          surface={surface}
          layout={surface.config?.prepacks?.layout_config}
          oddsFormat={oddsFormat}
          readOnly={surface.betting_mode !== 'wapi_kambi'}
          preview
        />
      </div>
    )
  }

  if (!isDisplayType(displayType)) {
    return fallback
  }

  const SurfaceComponent = SURFACE_REGISTRY[displayType]

  return (
    <WidgetPreviewProviders
      branding={branding}
      displayType={displayType}
      surface={surface}
      organizationSlug={organizationSlug}
      items={items}
      showBettingChrome={showBettingChrome}
      viewerToken={viewerToken}
    >
      <Suspense fallback={fallback}>
        <div data-widget-surface-preview={displayType}>
          <SurfaceComponent
            key={`${surface?.slug || `preview-${displayType}`}:${displayType}`}
            organization={organizationSlug || PREVIEW_ORG}
            apiUrl={PREVIEW_API_URL}
            surface={surface?.slug || `preview-${displayType}`}
          />
        </div>
      </Suspense>
    </WidgetPreviewProviders>
  )
}
